import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../utils/supabaseUtils';

// Create the auth context
const AuthContext = createContext(null);

// Create a provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check for an existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Get the current session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting session:', error);
          return;
        }
        
        if (data.session) {
          setUser(data.session.user);
          setSession(data.session);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Set up auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setUser(session.user);
        setSession(session);
        setIsAuthenticated(true);
        
        // Log login on sign in event
        if (event === 'SIGNED_IN') {
          logUserLogin(session.user.id);
        }
      } else {
        setUser(null);
        setSession(null);
        setIsAuthenticated(false);
      }
      
      setLoading(false);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Function to log user login
  const logUserLogin = async (userId) => {
    try {
      // Get IP address (optional)
      const ipResponse = await fetch('https://api.ipify.org?format=json')
        .catch(() => ({ json: () => Promise.resolve({ ip: 'unknown' }) }));
      const ipData = await ipResponse.json();
      
      // Log the login
      await supabase.rpc('log_user_login', { 
        p_user_id: userId,
        p_ip_address: ipData.ip || 'unknown',
        p_user_agent: navigator.userAgent || 'unknown'
      });
      
      console.log('Login logged successfully');
    } catch (err) {
      console.warn('Failed to log user login:', err);
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Value object to be provided to consumers
  const value = {
    user,
    session,
    isAuthenticated,
    loading,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 